import { useEffect, useState } from "react";
import api, { mediaUrl } from "@/lib/api";
import { LargeTitle, Surface, AButton, Pill, EmptyState, Segmented } from "@/components/ios";
import { SignaturePad } from "@/components/SignaturePad";
import { compressImage } from "@/lib/image";
import { KeyRound, Plus, Camera, MapPin, Link2, X, Trash2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "tenant", label: "Tenant" },
  { value: "trade", label: "Trade" },
  { value: "owner", label: "Owner" },
  { value: "agent", label: "Agent" },
];

export default function Handovers() {
  const [list, setList] = useState(null);
  const [props, setProps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [sel, setSel] = useState(null);

  const load = async () => { const { data } = await api.get("/handovers"); setList(data); };
  useEffect(() => { load(); api.get("/properties").then(({ data }) => setProps(data)); }, []);

  return (
    <div className="pb-10">
      <LargeTitle subtitle="Record key handovers with photo, GPS and signed proof"
        right={<AButton testid="new-handover-btn" onClick={() => setShowForm((v) => !v)}><Plus size={16} /> New handover</AButton>}>
        Key Handovers
      </LargeTitle>

      {showForm && <NewHandoverForm props={props} onDone={() => { setShowForm(false); load(); }} />}

      {list === null ? (
        <Surface className="p-6 text-secondary mt-4">Loading…</Surface>
      ) : list.length === 0 ? (
        <div className="mt-4"><EmptyState icon={KeyRound} title="No handovers yet" subtitle="Create a handover to capture proof of keys exchanged." /></div>
      ) : (
        <div className="space-y-3 mt-4">
          {list.map((h) => (
            <Surface key={h.id} className="p-4 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setSel(h)} testid={`handover-${h.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: "var(--text)" }}>{h.property?.address || "Property"}</div>
                  <div className="text-sm text-secondary">{h.direction === "return_in" ? "Return" : "Handover"} to {h.to_party?.name} · {h.total_sets} key(s) · {new Date(h.created_at).toLocaleDateString("en-AU")}</div>
                </div>
                <Pill tone={h.status === "completed" ? "success" : "warning"}>{h.status === "completed" ? "Signed" : "Pending"}</Pill>
              </div>
            </Surface>
          ))}
        </div>
      )}

      {sel && <HandoverDrawer initial={sel} onClose={() => setSel(null)} onChange={load} />}
    </div>
  );
}

function NewHandoverForm({ props, onDone }) {
  const [propertyId, setPropertyId] = useState("");
  const [direction, setDirection] = useState("handover_out");
  const [name, setName] = useState("");
  const [role, setRole] = useState("tenant");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [keys, setKeys] = useState([{ label: "Front door", count: 1 }]);
  const [description, setDescription] = useState("");
  const [gps, setGps] = useState(null);
  const [saving, setSaving] = useState(false);

  const captureGps = () => navigator.geolocation?.getCurrentPosition(
    (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); toast.success("Location captured"); },
    () => toast.error("Could not get location — check permissions"));

  const setKey = (i, patch) => setKeys((ks) => ks.map((k, idx) => (idx === i ? { ...k, ...patch } : k)));

  const create = async () => {
    if (!propertyId || !name.trim()) return toast.error("Property and recipient name are required");
    setSaving(true);
    try {
      await api.post("/handovers", {
        property_id: propertyId, direction, to_party: { name: name.trim(), role, email: email.trim() || null, phone: phone.trim() || null },
        keys: keys.filter((k) => k.label.trim()), description, gps,
      });
      toast.success("Handover created — now capture proof & signature");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  const inp = { background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" };

  return (
    <Surface className="p-5 mt-4 space-y-4" testid="handover-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-secondary">Property</label>
          <select data-testid="handover-property" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
            className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp}>
            <option value="">Select property…</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.address}, {p.suburb}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-secondary">Direction</label>
          <div className="mt-1"><Segmented value={direction} onChange={setDirection}
            options={[{ value: "handover_out", label: "Handover" }, { value: "return_in", label: "Return" }]} /></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-secondary">Recipient name</label>
          <input data-testid="handover-recipient" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Tenant"
            className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp} />
        </div>
        <div>
          <label className="text-xs font-semibold text-secondary">Recipient type</label>
          <select data-testid="handover-role" value={role} onChange={(e) => setRole(e.target.value)}
            className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp}>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-secondary">Keys / fobs</label>
        <div className="space-y-2 mt-1">
          {keys.map((k, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={k.label} onChange={(e) => setKey(i, { label: e.target.value })} placeholder="Label (e.g. Garage remote)"
                className="flex-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp} />
              <input type="number" min="1" value={k.count} onChange={(e) => setKey(i, { count: parseInt(e.target.value) || 1 })}
                className="w-16 text-sm rounded-lg px-3 py-2 outline-none border" style={inp} />
              <button type="button" onClick={() => setKeys((ks) => ks.filter((_, idx) => idx !== i))} className="text-secondary p-2"><Trash2 size={15} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setKeys((ks) => [...ks, { label: "", count: 1 }])} className="text-sm accent-text flex items-center gap-1"><Plus size={14} /> Add key</button>
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-secondary">Notes (optional)</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. 3 sets total, alarm code provided verbally"
          className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp} />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-secondary">Recipient email (for reminders)</label>
          <input data-testid="handover-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com"
            className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp} />
        </div>
        <div>
          <label className="text-xs font-semibold text-secondary">Recipient mobile (for SMS)</label>
          <input data-testid="handover-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+61 4XX XXX XXX"
            className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={inp} />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button type="button" data-testid="handover-gps" onClick={captureGps} className="text-sm flex items-center gap-1.5 rounded-full px-3 py-2 border" style={inp}>
          <MapPin size={14} /> {gps ? "Location ✓" : "Capture location"}
        </button>
        <AButton testid="handover-create" onClick={create} disabled={saving} className="ml-auto">{saving ? "Creating…" : "Create handover"}</AButton>
      </div>
    </Surface>
  );
}

function HandoverDrawer({ initial, onClose, onChange }) {
  const [h, setH] = useState(initial);
  const [signName, setSignName] = useState(initial.to_party?.name || "");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState({ email: false, sms: false });

  const refresh = async () => { const { data } = await api.get(`/handovers/${initial.id}`); setH(data); onChange && onChange(); };
  useEffect(() => { refresh(); api.get("/notifications/status").then(({ data }) => setChannels(data)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const sendReminder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/handovers/${h.id}/remind`, {});
      if (data.ok) toast.success("Reminder sent to recipient");
      else toast.error("Could not send — check email/mobile & provider keys");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Reminder failed"); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const compressed = await compressImage(reader.result);
        const gps = await new Promise((res) => navigator.geolocation
          ? navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null))
          : res(null));
        await api.post(`/handovers/${h.id}/media`, { data_url: compressed, gps });
        toast.success("Proof photo added");
        refresh();
      } catch (er) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(f);
  };

  const sign = async () => {
    if (!signName.trim() || !sig) return toast.error("Enter name and sign");
    setBusy(true);
    try {
      const gps = await new Promise((res) => navigator.geolocation
        ? navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null))
        : res(null));
      await api.post(`/handovers/${h.id}/sign`, { name: signName.trim(), role: h.to_party?.role, signature_image: sig, gps });
      toast.success("Handover signed");
      refresh();
    } catch (e) { toast.error("Could not sign"); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/handover/${h.portal_token}`);
    toast.success("Receiver signing link copied");
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-lg h-full overflow-y-auto p-5" style={{ background: "var(--bg)" }} onClick={(e) => e.stopPropagation()} data-testid="handover-drawer">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Handover proof</h2>
          <button onClick={onClose} className="text-secondary p-2"><X size={20} /></button>
        </div>

        <Surface className="p-4 mb-4">
          <div className="font-medium" style={{ color: "var(--text)" }}>{h.property?.address}</div>
          <div className="text-sm text-secondary">{h.direction === "return_in" ? "Return" : "Handover"} to {h.to_party?.name} ({h.to_party?.role})</div>
          <div className="text-sm text-secondary mt-1">{(h.keys || []).map((k) => `${k.label} ×${k.count}`).join(", ")} · {h.total_sets} total</div>
          {h.description && <div className="text-sm text-secondary mt-1">{h.description}</div>}
          {h.gps && <div className="text-xs text-secondary mt-1 flex items-center gap-1"><MapPin size={11} /> {h.gps.lat?.toFixed(5)}, {h.gps.lng?.toFixed(5)}</div>}
          <Pill tone={h.status === "completed" ? "success" : "warning"} className="mt-2">{h.status === "completed" ? "Signed" : "Pending signature"}</Pill>
        </Surface>

        <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2">Proof photos</div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {(h.media || []).map((m) => <img key={m.id} src={mediaUrl(m.signed_url)} alt="proof" className="w-full aspect-square object-cover rounded-lg" />)}
        </div>
        <label className="flex items-center justify-center gap-2 text-sm rounded-xl border-2 border-dashed py-3 cursor-pointer text-secondary mb-4" style={{ borderColor: "var(--border)" }} data-testid="handover-add-photo">
          <Camera size={16} /> Add photo of keys
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
        </label>

        {(h.signatures || []).length > 0 && (
          <Surface className="p-4 mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2 flex items-center gap-1"><CheckCircle2 size={13} className="accent-text" /> Signatures</div>
            {h.signatures.map((s) => (
              <div key={s.id} className="text-sm text-secondary">{s.signer} ({s.signer_role}) — {new Date(s.at).toLocaleString("en-AU")}</div>
            ))}
          </Surface>
        )}

        <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2">Sign for receipt (on device)</div>
        <input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Full name of person receiving keys" data-testid="handover-sign-name"
          className="w-full text-sm rounded-lg px-3 py-2 outline-none border mb-2" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
        <SignaturePad testid="handover-signature" onChange={setSig} />
        <AButton testid="handover-sign-btn" className="w-full mt-3" onClick={sign} disabled={busy}>{busy ? "Saving…" : "Confirm & sign"}</AButton>

        <button type="button" onClick={copyLink} data-testid="handover-copy-link" className="w-full mt-2 text-sm flex items-center justify-center gap-1.5 rounded-xl py-2.5 border" style={{ borderColor: "var(--border)", color: "var(--text)" }}>
          <Link2 size={14} /> Copy signing link for receiver
        </button>

        {h.status !== "completed" && (channels.email || channels.sms) && (h.to_party?.email || h.to_party?.phone) && (
          <button type="button" onClick={sendReminder} disabled={busy} data-testid="handover-remind-btn" className="w-full mt-2 text-sm flex items-center justify-center gap-1.5 rounded-xl py-2.5 accent-bg text-white">
            <Send size={14} /> {busy ? "Sending…" : `Send reminder${h.reminder_count ? ` (sent ${h.reminder_count}×)` : ""}`}
          </button>
        )}
        {h.status !== "completed" && !channels.email && !channels.sms && (
          <p className="text-xs text-secondary text-center mt-2">Set up email/SMS keys on the server to enable reminders.</p>
        )}
      </div>
    </div>
  );
}
